/**
 * Fullscreen helpers. Browsers only grant fullscreen from a user gesture, so
 * `enableOnFirstGesture` defers the request to the first tap.
 */
export function isFullscreen(): boolean {
  return document.fullscreenElement != null;
}

export async function requestFullscreen(el: Element = document.documentElement): Promise<void> {
  if (isFullscreen()) return;
  try {
    await el.requestFullscreen?.({ navigationUI: 'hide' });
  } catch {
    /* user denied or unsupported — sketches still run windowed */
  }
}

/**
 * Arm a one-shot listener that goes fullscreen on the first tap/key.
 *
 * We listen on `click`, NOT `pointerdown`: requesting fullscreen during the
 * pointerdown of a tap relayouts the page mid-gesture and the browser swallows
 * the resulting `click`, so a menu card's navigation (a click handler) is lost
 * on the first tap. On `click` the card's own handler has already fired (target
 * phase) before this bubble-phase handler runs, so navigation survives and we
 * still go fullscreen on the first tap.
 */
export function enableOnFirstGesture(el: Element = document.documentElement): () => void {
  const handler = () => {
    void requestFullscreen(el);
    teardown();
  };
  const teardown = () => {
    window.removeEventListener('click', handler);
    window.removeEventListener('keydown', handler);
  };
  window.addEventListener('click', handler);
  window.addEventListener('keydown', handler);
  return teardown;
}
